import React, { useEffect, useRef, useState } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';

import { Button, Typography } from '../../../components/atoms';
import { Add as AddIcon } from '../../../components/icons';
import { LoadingSpinner } from '../../../components/layouts';

type Props = {
  addLabel: React.ReactNode;
  fdKey: string;
  handleOnAdd: () => void;
  changeLabel?: React.ReactNode;
  handleOnChange?: () => void;
  helperText?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  setting?: JSX.Element;
};

const useStyles = (settingHeight) =>
  makeStyles((theme: Theme) => ({
    addHelperText: {
      marginTop: theme.spacing(0.5),
    },
    addSetting: {
      margin: theme.spacing(1.5, 0),

      '& .MuiButton-text': {
        borderBottom: 'none',

        '& .buttonState': {
          color: theme.palette.primary.main,
          cursor: 'pointer',
          fontSize: theme.typography.pxToRem(18),
          fontWeight: theme.typography.fontWeightMedium,

          '& .fd-icon__add': {
            color: theme.palette.primary.main,
          },
        },
        '&.Mui-disabled': {
          '& .buttonState': {
            color: theme.palette.grey[600],

            '& .fd-icon__add': {
              color: theme.palette.grey[600],
            },
          },
        },
      },
    },
    changeHelperText: {
      marginTop: theme.spacing(1),
    },
    changeSetting: {
      cursor: 'pointer',

      '& .MuiButton-label': {
        fontSize: theme.typography.pxToRem(13),
      },
    },
    loadingPanel: {
      minHeight: `${settingHeight}px`,
    },
    settingEditor: {
      display: 'flex',
      flexFlow: 'column nowrap',
      justifyContent: 'center',
      minHeight: `${settingHeight}px`,
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(2),
    },
  }))();

export function SettingEditor(props: Props): JSX.Element {
  const {
    addLabel,
    changeLabel,
    fdKey,
    handleOnAdd,
    handleOnChange,
    helperText,
    isDisabled = false,
    isLoading = false,
    setting = undefined,
  } = props;

  const [settingHeight, setSettingHeight] = useState(0);
  const classes = useStyles(settingHeight);
  const settingRef = useRef(null);

  useEffect(() => {
    if (settingRef.current) {
      setSettingHeight(settingRef.current.offsetHeight);
    }
  }, [isLoading, setting]);

  const renderHelperText = (style: string): JSX.Element => (
    <Typography className={style} variant="body2-secondary">
      {helperText}
    </Typography>
  );

  const renderChangeSetting = (): JSX.Element => (
    <>
      <div className={classes.changeSetting}>
        <Button
          data-fd={`edit-${fdKey}-link`}
          disabled={isDisabled}
          onClick={handleOnChange}
          variant="text"
        >
          {changeLabel}
        </Button>
      </div>
      {isDisabled && helperText && renderHelperText(classes.changeHelperText)}
    </>
  );

  const renderAddSetting = (): JSX.Element => (
    <div className={classes.addSetting}>
      <Button
        data-fd={`add-${fdKey}-link`}
        disabled={isDisabled}
        onClick={handleOnAdd}
        startIcon={<AddIcon />}
        variant="text"
      >
        {addLabel}
      </Button>
      {isDisabled && helperText && renderHelperText(classes.addHelperText)}
    </div>
  );

  if (isLoading) {
    return <LoadingSpinner className={classes.loadingPanel} variant="panel" />;
  }

  const hasSetting = !!setting;
  const isEditable = hasSetting && !!handleOnChange && !!changeLabel;

  return (
    <div className={classes.settingEditor} ref={settingRef}>
      {hasSetting && setting}
      {!hasSetting && renderAddSetting()}
      {isEditable && renderChangeSetting()}
    </div>
  );
}
